import { useState, useCallback, useRef } from 'react';
import movementAnalyticsService from '../services/movementAnalyticsService';
import i18n from '../i18n';

const useMovementAnalytics = () => {
    const [summary, setSummary] = useState(null);
    const [products, setProducts] = useState([]);
    const [productsTotal, setProductsTotal] = useState(0);

    // Drill-down (modal) state — level 1: Sub Category → Product (size) split (2d)
    const [sizeSplit, setSizeSplit] = useState(null);
    const [sizeSplitLoading, setSizeSplitLoading] = useState(false);
    const [sizeSplitError, setSizeSplitError] = useState(null);

    // Drill-down level 2: Product (size) → Orders/POs (2e)
    const [productOrders, setProductOrders] = useState(null);
    const [productOrdersLoading, setProductOrdersLoading] = useState(false);
    const [productOrdersError, setProductOrdersError] = useState(null);

    // CSV export of the size split
    const [exporting, setExporting] = useState(false);

    const [fetching, setFetching] = useState(false);
    const [error, setError] = useState(null);

    const summaryAbortRef = useRef(null);
    const productsAbortRef = useRef(null);
    const sizeSplitAbortRef = useRef(null);
    const productOrdersAbortRef = useRef(null);

    const fetchSummary = useCallback(async (params = {}) => {
        if (summaryAbortRef.current) summaryAbortRef.current.abort();
        const controller = new AbortController();
        summaryAbortRef.current = controller;

        setFetching(true);
        setError(null);
        try {
            const result = await movementAnalyticsService.getSummary(params, { signal: controller.signal });
            if (result.isCanceled) return;
            if (result.success) {
                setSummary(result.data ?? null);
            } else {
                setError(result.error);
                setSummary(null);
            }
            return result;
        } catch (err) {
            setError(err.message || i18n.t('hookErrors.movementAnalytics.fetchSummary'));
        } finally {
            if (summaryAbortRef.current === controller) {
                setFetching(false);
                summaryAbortRef.current = null;
            }
        }
    }, []);

    const fetchProducts = useCallback(async (params = {}) => {
        if (productsAbortRef.current) productsAbortRef.current.abort();
        const controller = new AbortController();
        productsAbortRef.current = controller;

        setFetching(true);
        setError(null);
        try {
            const result = await movementAnalyticsService.getProducts(params, { signal: controller.signal });
            if (result.isCanceled) return;
            if (result.success) {
                const raw = result.data;
                setProducts(raw?.products ?? []);
                setProductsTotal(raw?.total ?? 0);
            } else {
                setError(result.error);
                setProducts([]);
            }
            return result;
        } catch (err) {
            setError(err.message || i18n.t('hookErrors.movementAnalytics.fetchProducts'));
        } finally {
            if (productsAbortRef.current === controller) {
                setFetching(false);
                productsAbortRef.current = null;
            }
        }
    }, []);

    // Level 1 — Sub Category → Product (size) split (catalogueId = catalogue/Sub Category id)
    const fetchSizeSplit = useCallback(async (catalogueId, params = {}) => {
        if (sizeSplitAbortRef.current) sizeSplitAbortRef.current.abort();
        const controller = new AbortController();
        sizeSplitAbortRef.current = controller;

        setSizeSplitLoading(true);
        setSizeSplitError(null);
        setSizeSplit(null);
        try {
            const result = await movementAnalyticsService.getSubCategoryProducts(catalogueId, params, { signal: controller.signal });
            if (result.isCanceled) return;
            if (result.success) {
                setSizeSplit(result.data ?? null);
            } else {
                setSizeSplitError(result.error);
            }
            return result;
        } catch (err) {
            setSizeSplitError(err.message || i18n.t('hookErrors.movementAnalytics.fetchProductSplit'));
        } finally {
            if (sizeSplitAbortRef.current === controller) {
                setSizeSplitLoading(false);
                sizeSplitAbortRef.current = null;
            }
        }
    }, []);

    // Level 2 — Product (size) → Orders/POs (sizeId = ProductSize id)
    const fetchProductOrders = useCallback(async (sizeId, params = {}) => {
        if (productOrdersAbortRef.current) productOrdersAbortRef.current.abort();
        const controller = new AbortController();
        productOrdersAbortRef.current = controller;

        setProductOrdersLoading(true);
        setProductOrdersError(null);
        setProductOrders(null);
        try {
            const result = await movementAnalyticsService.getProductOrders(sizeId, params, { signal: controller.signal });
            if (result.isCanceled) return;
            if (result.success) {
                setProductOrders(result.data ?? null);
            } else {
                setProductOrdersError(result.error);
            }
            return result;
        } catch (err) {
            setProductOrdersError(err.message || i18n.t('hookErrors.movementAnalytics.fetchProductOrders'));
        } finally {
            if (productOrdersAbortRef.current === controller) {
                setProductOrdersLoading(false);
                productOrdersAbortRef.current = null;
            }
        }
    }, []);

    // Export the size split as CSV. Returns { success, data(blob), headers } / { success:false, error }.
    const exportSizeSplit = useCallback(async (catalogueId, params = {}) => {
        setExporting(true);
        try {
            return await movementAnalyticsService.exportSubCategoryProducts(catalogueId, params);
        } catch (err) {
            return { success: false, error: err.message || i18n.t('hookErrors.sales.export') };
        } finally {
            setExporting(false);
        }
    }, []);

    const clearDrilldown = useCallback(() => {
        if (sizeSplitAbortRef.current) sizeSplitAbortRef.current.abort();
        if (productOrdersAbortRef.current) productOrdersAbortRef.current.abort();
        setSizeSplit(null);
        setSizeSplitError(null);
        setSizeSplitLoading(false);
        setProductOrders(null);
        setProductOrdersError(null);
        setProductOrdersLoading(false);
    }, []);

    const clearProductOrders = useCallback(() => {
        if (productOrdersAbortRef.current) productOrdersAbortRef.current.abort();
        setProductOrders(null);
        setProductOrdersError(null);
        setProductOrdersLoading(false);
    }, []);

    return {
        summary,
        products,
        productsTotal,
        // drill-down level 1 (size split)
        sizeSplit,
        sizeSplitLoading,
        sizeSplitError,
        // drill-down level 2 (orders)
        productOrders,
        productOrdersLoading,
        productOrdersError,
        fetching,
        error,
        fetchSummary,
        fetchProducts,
        fetchSizeSplit,
        fetchProductOrders,
        exporting,
        exportSizeSplit,
        clearDrilldown,
        clearProductOrders,
    };
};

export default useMovementAnalytics;
